import React from 'react';
import { ReactComponent as BOBLogo } from '../../../Assets/BOB/logo.svg';
import MuthootLogo from '../../../Assets/Muthoot/Logo-White.png';
import { ReactComponent as Logo } from '../../../Assets/Jar/jar-horizontal-logo.svg';
import { getTenant } from '../../../Utils/storageUtils';
import { TenantType } from '../../../Utils/types';
import ProfileHeader, { ProfileHeaderProps } from '../Header/ProfileHeader';
import NavigationHeader, {
  NavigationHeaderProps,
} from '../Header/NavigationHeader';
import { ReactComponent as BankAccountBOB } from '../../../Assets/BOB/bank-acc.svg';
import { ReactComponent as FixedDepositBOB } from '../../../Assets/BOB/fixed-deposit.svg';
import { ReactComponent as MutualFundBOB } from '../../../Assets/BOB/mutual-fund.svg';
import { ReactComponent as StockBOB } from '../../../Assets/BOB/portfolio-stocks.svg';
import { ReactComponent as BankAccount } from '../../../Assets/CommonTenant/bankAccounts.svg';
import { ReactComponent as PensionWallet } from '../../../Assets/CommonTenant/categoryIcons/wallet and digital payments.svg';
import { ReactComponent as EPF } from '../../../Assets/CommonTenant/epf.svg';
import { ReactComponent as FixedDeposit } from '../../../Assets/CommonTenant/fd.svg';
import { ReactComponent as MutualFund } from '../../../Assets/CommonTenant/mf.svg';
import { ReactComponent as Stock } from '../../../Assets/CommonTenant/stock.svg';
import { ReactComponent as StableMoneyLogo } from '../../../Assets/StableMoney/logo.svg';
import NetWorthHeader, { NetWorthHeaderProps } from '../Header/NetWorthHeader';
import { AssetCategory, DashboardAssetType } from './constants';

type GetAssetDashboardHeaderProps = {
  profileHeaderProps?: ProfileHeaderProps;
  navigationHeaderProps?: NavigationHeaderProps;
  netWorthHeaderProps?: NetWorthHeaderProps;
};

export const getTenantLogo = (): React.ReactNode => {
  const tenant = getTenant();
  switch (tenant) {
    case TenantType.BOB:
    case TenantType.BOB_V2:
      return <BOBLogo height={50} width={50} />;
    case TenantType.MUTHOOT:
      return <img src={MuthootLogo} alt='logo' width={'35%'} />;
    case TenantType.STABLEMONEY:
      return (
        <StableMoneyLogo
          height={40}
          style={{ color: 'var(--header-colour)' }}
        />
      );
    case TenantType.JAR:
    default:
      return <Logo />;
  }
};

export const getAssetLogo = (
  assetType: DashboardAssetType
): React.ReactNode => {
  const tenant = getTenant();
  switch (tenant) {
    case TenantType.BOB:
    case TenantType.BOB_V2:
    case TenantType.MUTHOOT:
    case TenantType.STABLEMONEY:
      switch (assetType) {
        case DashboardAssetType.BANKS:
          return (
            <BankAccountBOB
              style={
                {
                  '--icon-bg-primary': 'var(--icon-asset-bg-primary)',
                  '--icon-bg-secondary': 'var(--icon-asset-bg-secondary)',
                  '--icon-secondary': 'var(--secondary-color)',
                  '--icon-primary': 'var(--primary-color)',
                } as React.CSSProperties
              }
            />
          );
        case DashboardAssetType.FD:
          return (
            <FixedDepositBOB
              style={
                {
                  '--icon-bg-primary': 'var(--icon-asset-bg-primary)',
                  '--icon-bg-secondary': 'var(--icon-asset-bg-secondary)',
                  '--icon-secondary': 'var(--secondary-color)',
                  '--icon-primary': 'var(--primary-color)',
                } as React.CSSProperties
              }
            />
          );
        case DashboardAssetType.MUTUAL_FUNDS:
          return (
            <MutualFundBOB
              style={
                {
                  '--icon-bg-primary': 'var(--icon-asset-bg-primary)',
                  '--icon-bg-secondary': 'var(--icon-asset-bg-secondary)',
                  '--icon-secondary': 'var(--secondary-color)',
                  '--icon-primary': 'var(--primary-color)',
                } as React.CSSProperties
              }
            />
          );
        case DashboardAssetType.STOCKS:
          return (
            <StockBOB
              style={
                {
                  '--icon-bg-primary': 'var(--icon-asset-bg-primary)',
                  '--icon-bg-secondary': 'var(--icon-asset-bg-secondary)',
                  '--icon-secondary': 'var(--secondary-color)',
                  '--icon-primary': 'var(--primary-color)',
                } as React.CSSProperties
              }
            />
          );
        case DashboardAssetType.NPS:
          return (
            <PensionWallet
              style={
                {
                  '--icon-bg-primary': 'var(--icon-asset-bg-primary)',
                  '--icon-bg-secondary': 'var(--icon-asset-bg-secondary)',
                  '--icon-secondary': 'var(--secondary-color)',
                  '--icon-primary': 'var(--primary-color)',
                } as React.CSSProperties
              }
            />
          );
        default:
          return <></>;
      }
    case TenantType.JAR:
    default:
      switch (assetType) {
        case DashboardAssetType.BANKS:
          return <BankAccount style={{ color: 'var(--secondary-color)' }} />;
        case DashboardAssetType.FD:
          return <FixedDeposit style={{ color: 'var(--secondary-color)' }} />;
        case DashboardAssetType.MUTUAL_FUNDS:
          return <MutualFund style={{ color: 'var(--secondary-color)' }} />;
        case DashboardAssetType.STOCKS:
          return <Stock style={{ color: 'var(--secondary-color)' }} />;
        case DashboardAssetType.EPFO:
          return <EPF style={{ color: 'var(--secondary-color)' }} />;
        default:
          return <></>;
      }
  }
};

export const getAAConsentText = (): string => {
  const tenant = getTenant();
  switch (tenant) {
    case TenantType.JAR:
      return `I hereby consent and authorize Jar to access and receive my financial information on a regular basis from various Financial Information Providers (FIPs) via Account Aggregators and utilize the above said financial information for below mentioned purposes: To provide me with personal finance management solutions detailing out my bank account balances, transaction analysis, budget-setting, statement download and other features built on top of it to help me analyze my financial goals and to send me information/personalized offers via email, text, call or online display or other means of delivery on Jar's services to meet these financial goals. I further confirm that I have verified the details of the request to transfer the data and understand that this consent is binding on me. I expressly disclaim Jar from any liability towards the correctness of the financial information/data and agree to indemnify and hold harmless Jar and any of its subsidiaries, successors, representatives, assigns and/or affiliates for any claims, demands, damages, actions, suits of any kind or nature which may arise whatsoever including for any discrepancy in the financial data. I also authorize the Account Aggregators to share my financial information with Jar and its service providers involved in providing Jar services solely for the aforementioned purposes. I further confirm that I have read and understood the consent provided hereinabove and agree to the same voluntarily and out of my free will. I also confirm that I am aware of the option to withdraw this consent at any time through Jar website. However such revocation shall be at my risks, costs and consequences in case the services have been partially or fully fulfilled by Jar.`;
    case TenantType.MUTHOOT:
      return `I hereby consent and authorize Muthoot Fincorp to access and receive my financial information on a regular basis from various Financial Information Providers (FIPs) via Account Aggregators and utilize the above said financial information for below mentioned purposes: To provide me with personal finance management solutions detailing out my bank account balances, transaction analysis, budget-setting, statement download and other features built on top of it to help me analyze my financial goals and to send me information/personalized offers via email, text, call or online display or other means of delivery on Muthoot's services to meet these financial goals. I further confirm that I have verified the details of the request to transfer the data and understand that this consent is binding on me. I expressly disclaim Muthoot Fincorp from any liability towards the correctness of the financial information/data and agree to indemnify and hold harmless Muthoot Fincorp and any of its subsidiaries, successors, representatives, assigns and/or affiliates for any claims, demands, damages, actions, suits of any kind or nature which may arise whatsoever including for any discrepancy in the financial data. I also authorize the Account Aggregators to share my financial information with Muthoot Fincorp and its service providers involved in providing Muthoot Fincorp services solely for the aforementioned purposes. I further confirm that I have read and understood the consent provided hereinabove and agree to the same voluntarily and out of my free will. I also confirm that I am aware of the option to withdraw this consent at any time through Muthoot Fincorp website. However such revocation shall be at my risks, costs and consequences in case the services have been partially or fully fulfilled by Muthoot Fincorp.`;
    case TenantType.STABLEMONEY:
      return `I hereby consent and authorize Stable Money to access and receive my financial information on a regular basis from various Financial Information Providers (FIPs) via Account Aggregators and utilize the above said financial information for below mentioned purposes: To provide me with personal finance management solutions detailing out my bank account balances, transaction analysis, budget-setting, statement download and other features built on top of it to help me analyze my financial goals and to send me information/personalized offers via email, text, call or online display or other means of delivery on Stable Money's services to meet these financial goals. I further confirm that I have verified the details of the request to transfer the data and understand that this consent is binding on me. I expressly disclaim Stable Money from any liability towards the correctness of the financial information/data and agree to indemnify and hold harmless Stable Money and any of its subsidiaries, successors, representatives, assigns and/or affiliates for any claims, demands, damages, actions, suits of any kind or nature which may arise whatsoever including for any discrepancy in the financial data. I also authorize the Account Aggregators to share my financial information with Stable Money and its service providers involved in providing Stable Money services solely for the aforementioned purposes. I further confirm that I have read and understood the consent provided hereinabove and agree to the same voluntarily and out of my free will. I also confirm that I am aware of the option to withdraw this consent at any time through Stable Money website. However such revocation shall be at my risks, costs and consequences in case the services have been partially or fully fulfilled by Stable Money.`;
    case TenantType.BOB:
    default:
      return `I hereby consent and authorize Bank of Baroda to access and receive my financial information on a regular basis from various Financial Information Providers (FIPs) via Account Aggregators and utilize the above said financial information for below mentioned purposes: To provide me with personal finance management solutions detailing out my bank account balances, transaction analysis, budget-setting, statement download and other features built on top of it to help me analyze my financial goals and to send me information/personalized offers via email, text, call or online display or other means of delivery on Bank of Baroda's services to meet these financial goals. I further confirm that I have verified the details of the request to transfer the data and understand that this consent is binding on me. I expressly disclaim Bank of Baroda from any liability towards the correctness of the financial information/data and agree to indemnify and hold harmless Bank of Baroda and any of its subsidiaries, successors, representatives, assigns and/or affiliates for any claims, demands, damages, actions, suits of any kind or nature which may arise whatsoever including for any discrepancy in the financial data. I also authorize the Account Aggregators to share my financial information with Bank of Baroda and its service providers involved in providing Bank of Baroda services solely for the aforementioned purposes. I further confirm that I have read and understood the consent provided hereinabove and agree to the same voluntarily and out of my free will. I also confirm that I am aware of the option to withdraw this consent at any time through Bank of Baroda website. However such revocation shall be at my risks, costs and consequences in case the services have been partially or fully fulfilled by Bank of Baroda.`;
  }
};

export const getTenantName = (): string => {
  const tenant = getTenant();
  switch (tenant) {
    case TenantType.JAR:
      return 'Jar';
    case TenantType.MUTHOOT:
      return 'Muthoot Fincorp';
    case TenantType.STABLEMONEY:
      return 'Stable Money';
    case TenantType.BOB:
    default:
      return 'Bank of Baroda';
  }
};

export const isCommonTenant = (): boolean => {
  const currentTenant = getTenant();
  if (currentTenant) {
    return [
      TenantType.JAR,
      TenantType.BOB,
      TenantType.MUTHOOT,
      TenantType.STABLEMONEY,
      TenantType.BOB_V2,
    ].includes(currentTenant);
  }
  return false;
};

export const showPoweredByIgnosis = (): boolean => {
  const tenant = getTenant();
  switch (tenant) {
    case TenantType.BOB:
    case TenantType.BOB_V2:
      return false;
    case TenantType.JAR:
    case TenantType.MUTHOOT:
    case TenantType.STABLEMONEY:
    default:
      return true;
  }
};

export const hasStocksAnalytics = (): boolean => {
  const tenant = getTenant();
  switch (tenant) {
    case TenantType.JAR:
    case TenantType.MUTHOOT:
    case TenantType.STABLEMONEY:
      return true;
    case TenantType.BOB:
    default:
      return false;
  }
};

export const hasMutualFundAnalytics = (): boolean => {
  const tenant = getTenant();
  switch (tenant) {
    case TenantType.JAR:
    case TenantType.MUTHOOT:
    case TenantType.STABLEMONEY:
      return true;
    case TenantType.BOB:
    default:
      return false;
  }
};

export const getChartColors = (): string[] => {
  const tenant = getTenant();
  switch (tenant) {
    case TenantType.BOB:
    case TenantType.BOB_V2:
      return [
        'var(--primary-color)',
        'var(--secondary-color)',
        '#FFF200',
        '#A78BFA',
        '#162B75',
        '#ED1C24',
      ];
    case TenantType.MUTHOOT:
    case TenantType.STABLEMONEY:
      return [
        'var(--chart-color-primary)',
        'var(--chart-color-secondary)',
        'var(--chart-color-tertiary)',
        'var(--chart-color-quaternary)',
        'var(--chart-color-quinary)',
      ];
    case TenantType.JAR:
    default:
      return [
        'var(--primary-color)',
        '#4ECDC4',
        '#FFD93D',
        '#A78BFA',
        '#F97316',
        '#EC4899',
      ];
  }
};

export const showNudgesCard = (): boolean => {
  const tenant = getTenant();
  switch (tenant) {
    case TenantType.BOB:
    case TenantType.BOB_V2:
      return true;
    case TenantType.JAR:
    case TenantType.MUTHOOT:
    case TenantType.STABLEMONEY:
    default:
      return false;
  }
};

export const showFDRDTopInterestRates = (): boolean => {
  const tenant = getTenant();
  switch (tenant) {
    case TenantType.BOB:
    case TenantType.BOB_V2:
      return false;
    case TenantType.JAR:
    case TenantType.MUTHOOT:
    case TenantType.STABLEMONEY:
    default:
      return true;
  }
};

export const showFDRDNudges = (): boolean => {
  const tenant = getTenant();
  switch (tenant) {
    case TenantType.BOB:
    case TenantType.BOB_V2:
      return false;
    case TenantType.JAR:
    case TenantType.MUTHOOT:
    case TenantType.STABLEMONEY:
    default:
      return false;
  }
};

export const showAssetDashboardDataNote = (): boolean => {
  const tenant = getTenant();
  switch (tenant) {
    case TenantType.BOB:
    case TenantType.BOB_V2:
      return true;
    case TenantType.JAR:
    case TenantType.MUTHOOT:
    case TenantType.STABLEMONEY:
    default:
      return false;
  }
};

export const showFinancialLiteracyQuizCard = (): boolean => {
  const tenant = getTenant();
  switch (tenant) {
    case TenantType.BOB:
    case TenantType.BOB_V2:
      return true;
    case TenantType.JAR:
    case TenantType.MUTHOOT:
    case TenantType.STABLEMONEY:
    default:
      return false;
  }
};

export const showBudgetCard = (): boolean => {
  const tenant = getTenant();
  switch (tenant) {
    case TenantType.BOB:
      return false;
    case TenantType.JAR:
    case TenantType.MUTHOOT:
    case TenantType.STABLEMONEY:
    default:
      return false;
  }
};

export const getSupportedAssetTypes = (): AssetCategory[] => {
  const tenant = getTenant();
  switch (tenant) {
    case TenantType.MUTHOOT:
    case TenantType.STABLEMONEY:
      return [
        AssetCategory.BANKS,
        AssetCategory.MUTUAL_FUNDS,
        AssetCategory.STOCKS,
        // AssetCategory.EPFO,
        // AssetCategory.FD,
        // AssetCategory.NPS,
      ];
    case TenantType.BOB:
    case TenantType.BOB_V2:
      return [
        AssetCategory.BANKS,
        AssetCategory.MUTUAL_FUNDS,
        AssetCategory.STOCKS,
        AssetCategory.FIXED_DEPOSIT,
        AssetCategory.NPS,
      ];
    case TenantType.JAR:
    default:
      return [AssetCategory.BANKS];
  }
};

export const getAssestDashboardHeader = ({
  profileHeaderProps,
  navigationHeaderProps,
  netWorthHeaderProps,
}: GetAssetDashboardHeaderProps): React.ReactNode => {
  const tenant = getTenant();
  switch (tenant) {
    case TenantType.BOB:
    case TenantType.BOB_V2:
      return profileHeaderProps ? (
        <ProfileHeader {...profileHeaderProps} />
      ) : null;
    case TenantType.STABLEMONEY:
      return netWorthHeaderProps ? (
        <NetWorthHeader {...netWorthHeaderProps} />
      ) : null;
    case TenantType.JAR:
    case TenantType.MUTHOOT:
    default:
      return navigationHeaderProps ? (
        <NavigationHeader {...navigationHeaderProps} />
      ) : null;
  }
};

export const supportAnalyticsApi = (): boolean => {
  const tenant = getTenant();
  switch (tenant) {
    case TenantType.BOB:
    case TenantType.BOB_V2:
      return false;
    case TenantType.JAR:
    case TenantType.MUTHOOT:
    case TenantType.STABLEMONEY:
    default:
      return true;
  }
};
